// components/MyProfile/Support/TicketHistory.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Pagination,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme,
    Collapse,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import { 
    ChevronDown, 
    ChevronUp, 
    RefreshCw, 
    Eye, 
    Clock, 
    CheckCircle, 
    AlertCircle,
    XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import zendeskService from '../../../services/zendeskService';

const TicketHistory = () => {
    const theme = useTheme();
    const { getAccessTokenSilently } = useAuth0();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // State
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [expandedTicket, setExpandedTicket] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    
    const perPage = 10;
    
    // Status configurations
    const statusConfig = {
        'new': { color: '#2196F3', icon: AlertCircle, label: 'New' },
        'open': { color: '#FF9800', icon: Clock, label: 'Open' },
        'pending': { color: '#9C27B0', icon: Clock, label: 'Pending' },
        'solved': { color: '#4CAF50', icon: CheckCircle, label: 'Solved' },
        'closed': { color: '#757575', icon: XCircle, label: 'Closed' }
    };
    
    // Fetch tickets
    const fetchTickets = async (currentPage = 1) => {
        try {
            setLoading(true);
            setError(null);
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const params = {
                page: currentPage,
                per_page: perPage,
                ...(statusFilter && { status: statusFilter })
            };
            
            const response = await zendeskService.getUserTickets(params, token);
            
            setTickets(response.data.tickets);
            setTotalCount(response.data.count);
            
            // Check for real-time updates for each ticket
            const ticketIds = response.data.tickets.map(t => t.id);
            checkTicketUpdates(ticketIds, token);
            
        } catch (err) {
            console.error('Error fetching tickets:', err);
            setError('Failed to load your tickets. Please try again.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };
    
    // Check for real-time updates
    const checkTicketUpdates = async (ticketIds, token) => {
        for (const ticketId of ticketIds) {
            try {
                const updateResponse = await zendeskService.getTicketUpdates(ticketId, token);
                if (updateResponse.data.update) {
                    // Update the ticket with real-time data
                    setTickets(prev => prev.map(ticket => 
                        ticket.id === ticketId 
                            ? { ...ticket, ...updateResponse.data.update.data }
                            : ticket
                    ));
                }
            } catch (err) {
                console.error(`Error checking updates for ticket ${ticketId}:`, err);
            }
        }
    };
    
    useEffect(() => {
        fetchTickets(page);
    }, [page, statusFilter]);
    
    const handlePageChange = (event, value) => {
        setPage(value);
    };
    
    const handleRefresh = () => {
        setRefreshing(true);
        fetchTickets(page);
    };
    
    const toggleExpanded = (ticketId) => {
        setExpandedTicket(expandedTicket === ticketId ? null : ticketId);
    };
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Mobile card view for tickets
    const MobileTicketCard = ({ ticket }) => {
        const status = statusConfig[ticket.status] || statusConfig['new'];
        const StatusIcon = status.icon;
        const isExpanded = expandedTicket === ticket.id;
        
        return (
            <Card
                component={motion.div}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{ mb: 2, overflow: 'hidden' }}
            >
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, pr: 1 }}>
                            {ticket.subject}
                        </Typography>
                        <Chip
                            icon={<StatusIcon size={14} />}
                            label={status.label}
                            size="small"
                            sx={{
                                bgcolor: `${status.color}20`,
                                color: status.color,
                                '& .MuiChip-icon': { color: status.color }
                            }}
                        />
                    </Box>
                    
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                        #{ticket.id} • {formatDate(ticket.created_at)}
                    </Typography>
                    
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                                    {ticket.description}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Last updated: {formatDate(ticket.updated_at)}
                                </Typography>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    
                    <Button
                        size="small"
                        onClick={() => toggleExpanded(ticket.id)}
                        endIcon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        sx={{ mt: 1, ml: -1 }}
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                </CardContent>
            </Card>
        );
    };
    
    if (loading && !refreshing) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                    Your Support Tickets
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            label="Status"
                        >
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(statusConfig).map(([value, config]) => (
                                <MenuItem key={value} value={value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <config.icon size={16} color={config.color} />
                                        {config.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <IconButton 
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{ 
                            bgcolor: 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' }
                        }}
                    >
                        <RefreshCw size={20} className={refreshing ? 'rotating' : ''} />
                    </IconButton>
                </Box>
            </Box>
            
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {tickets.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        No tickets found
                    </Typography>
                    <Typography color="textSecondary">
                        {statusFilter ? `No ${statusConfig[statusFilter]?.label.toLowerCase()} tickets` : 'You haven\'t submitted any support tickets yet'}
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Desktop Table View */}
                    {!isMobile ? (
                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Ticket ID</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Priority</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Updated</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tickets.map((ticket) => {
                                        const status = statusConfig[ticket.status] || statusConfig['new'];
                                        const StatusIcon = status.icon;
                                        
                                        return (
                                            <React.Fragment key={ticket.id}>
                                                <TableRow 
                                                    hover
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'action.hover' }
                                                    }}
                                                >
                                                    <TableCell>#{ticket.id}</TableCell>
                                                    <TableCell sx={{ maxWidth: 300 }}>
                                                        <Typography variant="body2" noWrap>
                                                            {ticket.subject}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={<StatusIcon size={14} />}
                                                            label={status.label}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: `${status.color}20`,
                                                                color: status.color,
                                                                '& .MuiChip-icon': { color: status.color }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                            {ticket.priority}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatDate(ticket.created_at)}</TableCell>
                                                    <TableCell>{formatDate(ticket.updated_at)}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => toggleExpanded(ticket.id)}
                                                        >
                                                            {expandedTicket === ticket.id ? 
                                                                <ChevronUp size={18} /> : 
                                                                <ChevronDown size={18} />
                                                            }
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
                                                        <Collapse in={expandedTicket === ticket.id}>
                                                            <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Description:
                                                                </Typography>
                                                                <Typography variant="body2" paragraph>
                                                                    {ticket.description}
                                                                </Typography>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        // Mobile Card View
                        <Stack spacing={2}>
                            {tickets.map((ticket) => (
                                <MobileTicketCard key={ticket.id} ticket={ticket} />
                            ))}
                        </Stack>
                    )}
                    
                    {/* Pagination */}
                    {totalCount > perPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={Math.ceil(totalCount / perPage)}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    )}
                </>
            )}
            
            <style>
                {`
                    @keyframes rotate {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .rotating {
                        animation: rotate 1s linear infinite;
                    }
                `}
            </style>
        </Box>
    );
};

export default TicketHistory;